/*
 * 共通関数
 * コンポーネントで使用するにはmixinsで読み込む
 * import cf from '@/mixins/commonFunctions';
 * mixins: [cf],
 */

import moment from 'moment';
import { cloneDeep } from 'lodash';

export default {
  /**
   * localStorage[${key}]の値を返却
   * @param  str key localStorageの対象キー
   * @return obj jsonパース済みオブジェクト
   */
  getLocalStorage(key) {
    let result;
    if (localStorage[key]) {
      result = JSON.parse(localStorage[key]);
    }
    return result;
  },


  /**
   * ローカルストレージに保存
   * @param obj 既存データにマージしたいオブジェクト
   * @param str key localStorageの対象キー
   */
  saveLocalStorage(obj, key = '') {
    if (!key) return;
    const ls = this.getLocalStorage(key);
    const save = ls ? { ...ls, ...obj } : obj;
    const stringify = JSON.stringify(save);
    localStorage.setItem(key, stringify);
  },


  /**
   * localStorage削除
   * @param key localStorageから削除するキー
   */
  deleteLocalStorage(key, key2 = null) {
    if (!key) return;
    if (localStorage[key]) {
      if (!key2) {
        localStorage.removeItem(key);
        console.log(`localStorage[${key}]を削除しました`);
      } else {
        // 特定の子階層のみを削除

        // 指定キー以外を一時ストック
        const stock = {};
        const target = JSON.parse(localStorage[key]);

        const keys = Object.keys(target);
        for (let i = 0; i < keys.length; i += 1) {
          const k = keys[i];
          if (k !== key2) stock[k] = target[k];
        }
        // 一度localstorage[key]ごと削除して
        localStorage.removeItem(key);
        // ストックしておいたkey2の除外データを保存
        this.saveLocalStorage(stock, key);
        console.log(`localStorage[${key}]の[${key2}]を削除しました`);
      }
    } else {
      console.log(`localStorage[${key}]は存在しません`);
    }
  },


  /**
   * 文字列判定
   * @param value
   * @return bool
   */
  isString(inputText) {
    return (typeof inputText === 'string' || inputText instanceof String);
  },


  /**
   * 数値判定（NaNやInfinityは無効）
   * @param value
   * @return bool
   */
  isNumber(value) {
    return Number.isFinite(value);
  },


  /**
   * 整数値の判定
   * @param str
   * @return bool
   */
  checkInteger(str) {
    if (!str) return false;
    return String(str).match(/^[+|-]?[0-9]+$/);
  },


  /**
   * 整数値か小数値の判定
   * @param str
   * @return bool
   */
  checkFloat(str) {
    if (!str) return false;
    return str.match(/^[-|+]?[0-9]*\.[0-9]+$|^[+|-]?[0-9]+$/);
  },


  /**
   * valueが整数値に変更可能かどうか
   * @param value
   * @return bool
   */
  convert2Num(str) {
    if (!str) return null;
    if (!this.checkInteger(str)) return null;
    const result = Number(str);
    return this.isNumber(result) ? result : null;
  },


  /** URLからページ番号取得 */
  getPageNum(route) {
    const query = route.query;
    const page = this.convert2Num(query.page);
    return page || 1;
  },


  /**
   * ページネーション配列の生成
   * @param object args
   *   currentPage
   *   lastPage
   * @return array
   *
   * パターン1
   *   合計ページ数が5未満
   * パターン2
   *   1 2 3 4 ... 50
   * パターン3
   *   1 ... 48 49 50
   * パターン4
   *   1 ... 3 4 5 ... 50
   */
  generatePagination(args) {
    const result = [];
    const last = args.lastPage;
    const prevNext = last > 5;

    // 1ページ完結の場合はスルー
    if (last === 1) return [];

    // prevボタン
    if (prevNext) {
      result.push({
        class: 'prev',
        icon: 'fal fa-angle-left',
        page: args.currentPage - 1,
      });
    }

    // パターン毎にpager作成
    const start = [1, 2, 3];
    const end = [(last - 2), (last - 1), last];
    const num = 4;
    if (!prevNext) {
      for (let i = 0; i < last; i += 1) {
        result.push({ page: i + 1 });
      }
    } else if (start.includes(args.currentPage)) {
      // パターン1
      for (let i = 0; i < num; i += 1) {
        result.push({ page: i + 1 });
      }
      result.push(
        { dot: true },
        { page: last },
      );
    } else if (end.includes(args.currentPage)) {
      // パターン2
      result.push(
        { page: 1 },
        { dot: true },
      );
      for (let i = 0; i < 4; i += 1) {
        result.push({ page: last - (num - i - 1) });
      }
    } else {
      // パターン3
      result.push(
        { page: 1 },
        { dot: true },
        { page: args.currentPage - 1 },
        { page: args.currentPage },
        { page: args.currentPage + 1 },
        { dot: true },
        { page: last },
      );
    }

    // nextボタン
    if (prevNext) {
      result.push({
        class: 'next',
        icon: 'fal fa-angle-right',
        page: args.currentPage + 1,
      });

      // 現ページが最初か最後の場合はdisabledクラス追加
      if (args.currentPage === 1) result[0].class = 'prev disabled';
      if (args.currentPage === last) result[result.length - 1].class = 'next disabled';
    }

    return result;
  },

  /**
   * 環境に応じてstripeのpublicKeyを返却
   */
  getStripePubKey() {
    let STRIPE_PUB_KEY = null;
    switch (process.env.NODE_ENV) {
      case 'production':
        STRIPE_PUB_KEY = process.env.VUE_APP_STRIPE_PUBLIC_KEY_PROD;
        break;
      default:
        STRIPE_PUB_KEY = process.env.VUE_APP_STRIPE_PUBLIC_KEY_DEV;
        break;
    }
    return STRIPE_PUB_KEY;
  },


  /**
   * 桁数を合わせる
   */
  zeroPadding(num, len) {
    return (Array(len).join('0') + num).slice(-len);
  },


  /**
   * 【重要】
   * コンポーネントで直接使用するには
   * methodsオブジェクトに入れておく必要がある
   */
  methods: {
    /** タイムスタンプのフォーマット */
    formatTimestamp(stamp, format = 'YYYY-MM-DD') {
      let result = moment(stamp, 'X').format(format);
      if (!String(stamp).includes('GMT')) {
        result = moment(stamp).format(format);
      }
      return result;
    },

    round1Decimal(number) {
      return Math.round(number * 10) / 10;
    },

    adjust10thousand(nubmer) {
      return nubmer / 10000;
    },

    truncateString(str) {
      let result = str;
      const excludeNames = ['STUDIOUS', 'UNITED TOKYO', 'PUBLIC TOKYO', 'THE TOKYO', 'CITY TOKYO', 'CONZ'];
      const targetStr = excludeNames.find((target) => str.includes(target));
      if (targetStr) {
        const idx = str.indexOf(targetStr);
        if (idx !== -1) result = str.slice(0, idx);
      }
      return result;
    },

    staffName(staff) {
      const lastName = this.truncateString(staff.last_name || '');
      const firstName = this.truncateString(staff.first_name || '');

      return `${lastName} ${firstName}`.trim();
    },

    rankingItem(type, target, item) {
      const rankingObj = {
        rank: item.rank || '--',
        name: type === 'personal' ? this.truncateString(item.name) : item.name,
        image: item.image,
        star: type === 'personal' ? item.beforeStar : null,
      };

      // 達成率
      if (target === 'rate') {
        rankingObj.value = item.rate * 100;
        rankingObj.note = type === 'personal' ? item.shop : `¥ ${(item.sales || 0).toLocaleString()} / ¥ ${(item.goal || 0).toLocaleString()}`;
        if (type === 'personal') rankingObj.valueNote = `¥ ${(item.sales || 0).toLocaleString()} / ¥ ${(item.goal || 0).toLocaleString()}`;
      }

      // 売上実績
      if (target === 'sales') {
        rankingObj.value = item.sales;
        rankingObj.note = type === 'personal' ? item.shop : `目標 ¥ ${(item.goal || 0).toLocaleString()}`;
      }

      // 顧客売上
      if (target === 'customer') {
        rankingObj.value = item.customer;
        rankingObj.note = type === 'personal' ? item.shop : `¥ ${(item.goal || 0).toLocaleString()}`;
        if (type === 'personal') rankingObj.valueNote = `全体 ¥ ${(item.sales || 0).toLocaleString()}`;
      }
      return rankingObj;
    },


    adjustChartData(chartData, unit = 10000) {
      const targetData = chartData;

      // 返却値の構造
      const returnObj = {
        ranges: [],
        data: [],
      };
      if (!(targetData?.length > 1)) return returnObj;

      // データの整形
      let maxValue = 0; // 最大値によって縦軸のrangeを調整するために保持
      let maxRate = 0;
      targetData.forEach((row, i) => {
        const newRow = [row[0]];
        if (i === 0) {
          // ヘッダー列の追加
          row.slice(1).forEach((val) => {
            newRow.push(val);
            newRow.push({ role: 'tooltip', type: 'string' });
          });
        } else {
          // データ列の追加
          row.slice(1).forEach((val, k) => {
            const newVal = val / (unit || 1);
            newRow.push(newVal);
            // tooltip(吹き出し)の調整
            const label = targetData[0][k + 1];
            const tooltip = (value, prefix, suffix) => `${label}: ${prefix}${value}${suffix}`;
            const tooltipVal = tooltip(
              k < 2 ? newVal.toLocaleString() : val.toFixed(1),
              k < 2 ? '¥' : '',
              k < 2 ? '万' : '%',
            );
            newRow.push(tooltipVal);
            // 最大値なら保持（k === 3 は達成率）
            if (k !== 2 && newVal > maxValue) maxValue = newVal;
            if (k === 2 && val > maxRate) maxRate = val;
          });
        }
        returnObj.data = [...returnObj.data, ...[newRow]];
      });

      // // maxValueの桁数
      const digitMaxValue = String(Math.floor(maxValue)).length;
      let rangeMax = 120;
      if (digitMaxValue > 2) {
        const target = String(Math.floor(maxValue)).slice(0, 2);
        let rangeMaxLeft = 120;
        if (target < 12) rangeMaxLeft = 12;
        else if (target < 24) rangeMaxLeft = 24;
        else if (target < 60) rangeMaxLeft = 60;
        rangeMax = rangeMaxLeft * 10 ** (digitMaxValue - 2);
      }

      const rangeArr = [0];
      for (let i = 1; i <= 6; i += 1) {
        rangeArr.push((rangeMax / 6) * i);
      }

      // maxRateの桁数
      const digitMaxRate = String(Math.floor(maxRate)).length;
      let rateRangeMax = 120;
      if (digitMaxRate > 2) {
        const target = String(Math.floor(maxRate)).slice(0, 2);
        let rangeMaxLeft = 120;
        if (target < 12) rangeMaxLeft = 12;
        else if (target < 24) rangeMaxLeft = 24;
        else if (target < 60) rangeMaxLeft = 60;
        rateRangeMax = rangeMaxLeft * 10 ** (digitMaxRate - 2);
      }

      const rateRangeArr = [0];
      for (let i = 1; i <= 6; i += 1) {
        rateRangeArr.push((rateRangeMax / 6) * i);
      }

      returnObj.ranges = rangeArr.map((val, i) => ({ v: val, f: chartData[0].length === 4 ? `${val.toLocaleString()}(${rateRangeArr[i]})` : `${val}` }));

      // rowの４番目の要素は軸が異なるためrangeのmaxの値をもとに調整を入れる
      // （shop, brandで調整が必要、personalの場合は要素が３番目までなので無視）
      returnObj.data.forEach((row, i) => {
        if (i !== 0) {
          row.forEach((val, j) => {
            if (j === 5) {
              returnObj.data[i][j] = (returnObj.ranges[5]?.v * val * unit) / (100 * (rateRangeArr[6] / 120));
            }
          });
        }
      });

      // [monthly] 値のない月のデータはnullにする
      if (returnObj.data.map((data) => data[0]).includes('1月')) {
        const isAllNull = !(returnObj.data.slice(1).find((row) => !(row.slice(1).every((val) => val === 0)))); // 値のある月がない
        returnObj.data.forEach((row, i) => {
          // 該当月の全ての値が0だったら
          if (row.slice(1).every((val) => val === 0)) {
            const newRow = [row[0]];
            for (let j = 0; j < returnObj.data[0].length - 1; j += 1) { newRow.push(null); }
            returnObj.data[i] = newRow;
          }
        });
        if (isAllNull) {
          const newRow = [returnObj.data[1][0]];
          for (let j = 0; j < returnObj.data[0].length - 1; j += 1) { newRow.push(0); }
          returnObj.data[1] = newRow;
        }
      }

      // [yearly] ~ 2027 までの感は2024が一番左になるように調整が必要
      if (returnObj.data.map((data) => data[0]).includes('2024')) {
        const hCount = 5; // yearlyのチャートに何年分表示するか
        const dataCount = returnObj.data[0].length - 1;
        const idxOf2024 = returnObj.data.map((data) => data[0]).indexOf('2024');
        const newData = [
          returnObj.data[0],
          ...returnObj.data.filter((_, i) => i >= idxOf2024),
        ];
        if (newData.length < hCount) {
          const addRowCount = hCount - (newData.length - 1);
          for (let i = 0; i < addRowCount; i += 1) {
            const newRow = [`${2024 + (hCount - addRowCount + i)}`];
            for (let j = 0; j < dataCount; j += 1) { newRow.push(null); }
            newData.push(newRow);
          }
        }
        returnObj.data = newData;
      }

      return returnObj;
    },

    /** シャッフルした配列を返却 */
    shuffle(array) {
      // 元の配列を変更しないためにコピーを作成
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1)); // 0からiまでのランダムなインデックス
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]; // 要素を入れ替える
      }
      return shuffled; // シャッフルされた配列を返す
    },

    /**
     * クレジットカードアイコン名
     * mastercard
     * visa
     * amex
     * jcb
     * discover
     * diners-club
     */
    getCardIconName(brand) {
      const lowercase = brand.toLowerCase();
      let result;
      switch (lowercase) {
        case 'american express': result = 'amex'; break;
        case 'diners club': result = 'diners-club'; break;
        default: result = lowercase; break;
      }
      return result;
    },

    /** クローンオブジェクトの返却 */
    getClone(object) {
      return cloneDeep(object);
    },

    /**
     * アイコンURL（type = 2）の返却
     * @param obj object
     *   ユーザ等特定idのオブジェクト
     */
    getMyIconUrl(object) {
      const defaultIcon = '/img/default/icon.png';
      let url = defaultIcon;

      if (!object) return url;

      const keys = Object.keys(object);
      if (!keys.includes('urls')) return url;

      const isArray = Array.isArray(object.urls);
      if (isArray) {
        // adjustUrls未適用（[{}, {}, ...]）
        const urls = object.urls || [];

        if (urls.length) {
          // item.type = 2（アイコン）画像を返却する
          urls.some((item) => {
            if (item.type === 2) {
              url = item.url || defaultIcon;
              return true;
            }
            return false;
          });
        }
      } else if (object.urls && object.urls.icon.length) {
        // adjustUrls適用済み（{ main: {}, icon: {}, ... }）
        url = object.urls.icon[0].url || defaultIcon;
      }

      return url;
    },

    /**
     * ユーザオブジェクトから表示ラベルを返却
     * // 1. usernameが存在する場合は優先
     * // 2. usernameなくfacebook.nameが存在する場合はfacebook.name
     * // 3. username・facebook.nameともにない場合はemail
     */
    getUserLabel(user) {
      let name = user.email;
      if (user.facebook) name = user.facebook.name;
      if (user.username) name = user.username;
      return name;
    },

    /**
     * アイキャッチ取得
     * data.eyecatchの有無でデフォ画像と振り分け
     */
    getEyecatch(data) {
      return data.eyecatch || '/img/default/default.jpg';
    },

    /** 文字列の中からimgタグとhtmlタグを除去 */
    replaceImgTag(str) {
      let result = str;
      if (str) {
        // imgタグをsrc丸ごと削除
        const images = str.match(/<img(.|\s)*?>/gi);
        if (images && images.length) {
          images.forEach((img) => {
            result = result.replace(img, '');
          });
        }
        // テキスト文中からhtmlタグを削除
        result = result
          .replace(/(<([^>]+)>)/gi, '')
          .replace(/\n/g, '')
          .replace(/&nbsp;/g, '');
      }
      return result;
    },

    /**
      * 3桁毎にカンマ割り振り
      * @param int num 元の数値
      */
    addComma(num, afterPoint = '', count = 0) {
      if (num !== 0 && !num) return '';
      // if (!this.isNumber(num)) return false;

      // 文字列にする
      let strNum = String(num);
      let afterDecimalPoint = afterPoint;
      if (strNum.includes('.')) {
        // 小数点以下を含む場合
        const split = strNum.split('.');
        strNum = split[0];
        afterDecimalPoint = `.${split[1]}`;
      }
      const len = Number(strNum.length);

      // 再帰的に呼び出し
      if (len > 3) {
        // 前半を引数に再帰呼び出し + 後半3桁
        strNum = `${this.addComma(strNum.substring(0, len - 3), afterDecimalPoint, count + 1)},${strNum.substring(len - 3)}`;
      }

      if (count === 0) {
        strNum += `${afterDecimalPoint}`;
      }

      return `${strNum}`;
    },


    /**
     * 任意の桁で四捨五入
     * @param {number} value 四捨五入する数値
     * @param {number} base どの桁で四捨五入するか（10→小数点第二位を四捨五入）
     * @return {number} 四捨五入した値
     */
    round(value, base = 1) {
      return Math.round(value * base) / base;
    },

    /**
     * 任意の桁で切り上げ
     * @param {number} value 切り上げする数値
     * @param {number} base どの桁で切り上げするか（10→小数点第二位を切り上げ）
     * @return {number} 切り上げした値
     */
    ceil(value, base = 1) {
      return Math.ceil(value * base) / base;
    },

    /**
     * 任意の桁で切り捨て
     * @param {number} value 切り捨てする数値
     * @param {number} base どの桁で切り捨てするか（10→小数点第二位を切り捨て））
     * @return {number} 切り捨てした値
     */
    floor(value, base = 1) {
      return Math.floor(value * base) / base;
    },

    /**
     * filter群
     */
    filterDate(date) {
      return moment(date).format('YYYY-MM-DD');
    },
    filterDate_ja(date) {
      return moment(date).format('YYYY年M月D日');
    },
    filterDatetime(date) {
      return moment(date).format('YYYY.M.D HH:mm');
    },
    filterNumber_format(value) {
      // valueを文字列に変換
      const str = String(value);
      if (!str.match(/^\d+$/)) {
        return value;
      }
      const formatter = new Intl.NumberFormat('ja-JP');
      return formatter.format(str);
    },
    filterTax_price(value) {
      let includeTax = value * 1.1;
      // 小数点以下切り捨て
      includeTax = this.roundDown(includeTax, 0);
      // 税込価格を文字列に変換
      const str = String(includeTax);
      if (!str.match(/^\d+$/)) {
        return includeTax;
      }
      const formatter = new Intl.NumberFormat('ja-JP');
      return `${formatter.format(str)}（税込）`;
    },
    filterZip(value) {
      const str = value ? String(value) : null;
      let result = '';
      if (str && str.includes('-')) {
        // ハイフンが含まれている場合はそのまま出力
        result = `〒${str}`;
      } else if (str && !str.includes('-')) {
        // ハイフンを付与して出力
        result = `〒${str.substr(0, 3)}-${str.substr(3, 4)}`;
      }
      return result;
    },
    filterRemove_hyphen(value) {
      const str = value.replace(/-/g, '');
      return str;
    },
    /** 表示文字数制限 */
    filterOmitted(limit, str) {
      const string = cloneDeep(str);
      if (string.length > limit) {
        return `${string.substr(0, limit)}...`;
      }
      return string;
    },
  },
};
